import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  Menu,
  Card,
  CardContent,
  Checkbox,
  Toolbar,
  alpha,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  SelectAll as SelectAllIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import api from '../services/api';

interface User {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: 'admin' | 'customer';
  client_id: string;
  company_name?: string;
  phone?: string;
  created_at: string;
  last_login?: string;
  is_active: boolean;
  pass_count?: number;
}

interface Company {
  company_id: string;
  company_name: string;
  email?: string;
}

interface UserFormData {
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'customer';
  company_name: string;
  company_id?: string; // Added for backend API
  phone: string;
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();

  // currentUser Management State
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // User Limit State
  const [userLimitStatus, setUserLimitStatus] = useState<{
    current_users: number;
    max_users: number;
    can_add_user: boolean;
    tier: string;
    percentage_used: number;
  } | null>(null);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState<{
    temporary_password: string;
    entra_id_reset: boolean;
    email_sent: boolean;
  } | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'customer',
    company_name: '',
    phone: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notification, setNotification] = useState<{message: string; severity: 'success' | 'error'} | null>(null);

  // API calls
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users/?active_only=false');
      return response.data.users || [];
    } catch (error) {
      logger.error('Error fetching users:', error);
      return [];
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await api.get('/companies/');
      return response.data || [];
    } catch (error) {
      logger.error('Error fetching companies:', error);
      return [];
    }
  }, []);

  const fetchUserLimitStatus = useCallback(async () => {
    try {
      const response = await api.get('/companies/me/user-limit-status').catch(() => null);
      return response ? response.data : null;
    } catch (error) {
      logger.error('Error fetching user limit status:', error);
      return null;
    }
  }, []);

  // Load users and companies
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [usersData, companiesData, limitStatus] = await Promise.all([
          fetchUsers(),
          fetchCompanies(),
          fetchUserLimitStatus()
        ]);
        setUsers(usersData);
        setCompanies(companiesData);
        setUserLimitStatus(limitStatus);
      } catch (error) {
        logger.error('Error loading data:', error);
        setNotification({ message: 'Failed to load user data. Please refresh the page.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchUsers, fetchCompanies, fetchUserLimitStatus]);

  // Filtered and paginated users
  const filteredUsers = useMemo(() => {
    const filtered = users.filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      return matchesSearch && matchesRole;
    });
    return filtered;
  }, [users, searchTerm, roleFilter]);

  const paginatedUsers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  // Role badge component
  const getRoleBadge = useCallback((role: string) => {
    const roleConfig = {
      admin: { color: 'error' as const, icon: <AdminIcon sx={{ fontSize: 16 }} />, label: 'Administrator' },
      customer: { color: 'primary' as const, icon: <BusinessIcon sx={{ fontSize: 16 }} />, label: 'Klijent' }
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.customer;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  }, []);

  // Form handlers
  const handleCreateUser = useCallback(() => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'customer',
      company_name: '',
      phone: ''
    });
    setErrors({});
    setIsCreateDialogOpen(true);
  }, []);

  const handleEditUser = useCallback((tableUser: User) => {
    setSelectedUser(tableUser);
    setFormData({
      email: tableUser.email,
      first_name: tableUser.first_name || '',
      last_name: tableUser.last_name || '',
      role: tableUser.role,
      company_name: tableUser.company_name || '',
      phone: tableUser.phone || ''
    });
    setErrors({});
    setIsEditDialogOpen(true);
    setActionMenuAnchor(null);
  }, []);

  const handleDeleteUser = useCallback((tableUser: User) => {
    setSelectedUser(tableUser);
    setIsDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  }, []);

  const handleResetPassword = useCallback((tableUser: User) => {
    setSelectedUser(tableUser);
    setIsResetPasswordDialogOpen(true);
    setResetPasswordResult(null);
    setActionMenuAnchor(null);
  }, []);

  const handleSubmitResetPassword = useCallback(async () => {
    if (!selectedUser) return;

    setSubmitLoading(true);
    try {
      const response = await api.post(`/users/${selectedUser.user_id}/reset-password`, {});
      setResetPasswordResult(response.data);

      // Show success notification
      setNotification({
        message: 'Password uspešno resetovan i email poslat korisniku',
        severity: 'success'
      });
    } catch (error) {
      logger.error('Error resetting password:', error);
      setNotification({
        message: error instanceof Error ? error.message : 'Greška pri resetovanju lozinke',
        severity: 'error'
      });
    } finally {
      setSubmitLoading(false);
    }
  }, [selectedUser]);

  const validateForm = useCallback((isCreate: boolean = false) => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email je obavezan';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      newErrors.email = 'Email format nije valjan';
    }

    if (!formData.first_name) {
      newErrors.first_name = 'Ime je obavezno';
    }

    if (!formData.last_name) {
      newErrors.last_name = 'Prezime je obavezno';
    }

    if (!formData.company_name) {
      newErrors.company_name = 'Naziv kompanije je obavezan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmitCreate = useCallback(async () => {
    if (!validateForm(true)) return;

    setSubmitLoading(true);
    try {
      // Find company_id from company_name
      const selectedCompany = companies.find(c => c.company_name === formData.company_name);
      if (!selectedCompany) {
        throw new Error('Selected company not found');
      }

      await api.post('/users/', {
        email: formData.email,
        role: formData.role,
        company_id: selectedCompany.company_id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || undefined
      });

      // Refresh users list
      const usersData = await fetchUsers();
      setUsers(usersData);

      // Refresh user limit status
      const limitStatus = await fetchUserLimitStatus();
      setUserLimitStatus(limitStatus);

      setIsCreateDialogOpen(false);
      setNotification({ message: 'Korisnik uspešno kreiran', severity: 'success' });

      // Reset form
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'customer',
        company_name: '',
        phone: ''
      });
    } catch (error) {
      logger.error('Error creating user:', error);
      setNotification({ message: 'Greška pri kreiranju korisnika', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  }, [formData, validateForm, fetchUsers, fetchUserLimitStatus, companies]);

  const handleSubmitEdit = useCallback(async () => {
    if (!validateForm(false) || !selectedUser) return;

    setSubmitLoading(true);
    try {
      await api.patch(`/users/${selectedUser.user_id}`, {
        email: formData.email,
        role: formData.role,
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      // Refresh users list
      const usersData = await fetchUsers();
      setUsers(usersData);
      setIsEditDialogOpen(false);
    } catch (error) {
      logger.error('Error updating user:', error);
      setNotification({ message: 'Greška pri ažuriranju korisnika', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  }, [formData, selectedUser, validateForm, fetchUsers]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedUser) return;

    setSubmitLoading(true);
    try {
      await api.delete(`/users/${selectedUser.user_id}?confirm=true`);

      // Refresh users list
      const usersData = await fetchUsers();
      setUsers(usersData);

      // Refresh user limit status
      const limitStatus = await fetchUserLimitStatus();
      setUserLimitStatus(limitStatus);

      setIsDeleteDialogOpen(false);
    } catch (error) {
      logger.error('Error deleting user:', error);
      setNotification({ message: 'Greška pri brisanju korisnika', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  }, [selectedUser, fetchUsers, fetchUserLimitStatus]);

  // Statistics
  const stats = useMemo(() => {
    const totalUsers = users.length;
    const adminCount = users.filter(u => u.role === 'admin').length;
    const customerCount = users.filter(u => u.role === 'customer').length;
    const activeCount = users.filter(u => u.is_active).length;

    return { totalUsers, adminCount, customerCount, activeCount };
  }, [users]);

  // Bulk selection functions
  const handleSelectAll = useCallback(() => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      const validUsers = filteredUsers.filter(user => user.user_id);
      const userIds = validUsers.map(user => user.user_id.toString());
      setSelectedUsers(new Set(userIds));
    }
  }, [selectedUsers, filteredUsers]);

  const handleSelectUser = useCallback((userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  }, [selectedUsers]);

  const handleBulkRoleUpdate = useCallback(async (newRole: string) => {
    if (selectedUsers.size === 0) return;

    setBulkActionLoading(true);
    try {
      // Get user data for selected users
      const selectedUserData = users.filter(user => selectedUsers.has(user.user_id));

      // Optimistic update - immediately update local state
      const userIdsToUpdate = selectedUserData.map(u => u.user_id);
      setUsers(prevUsers =>
        prevUsers.map(user =>
          userIdsToUpdate.includes(user.user_id)
            ? { ...user, role: newRole as 'admin' | 'customer' }
            : user
        )
      );

      // API call to update user roles for each selected user
      await Promise.all(
        selectedUserData.map(user =>
          api.patch(`/users/${user.user_id}`, {
            email: user.email,
            role: newRole,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            is_active: user.is_active
          })
        )
      );

      // Refresh users to ensure data consistency
      await fetchUsers();

      // Clear selection after successful update
      setSelectedUsers(new Set());

    } catch (error) {
      logger.error('Error updating user roles:', error);
      // Revert optimistic update on error
      await fetchUsers();
      // Show error message to user
      alert(`Greška pri ažuriranju uloga korisnika: ${error}`);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedUsers, users, fetchUsers]);

  // Helper function to perform the actual bulk status update
  const performBulkStatusUpdate = useCallback(async (isActive: boolean, usersToUpdate: User[]) => {
    // Optimistic update - immediately update local state
    const userIdsToUpdate = usersToUpdate.map(u => u.user_id);
    setUsers(prevUsers =>
      prevUsers.map(user =>
        userIdsToUpdate.includes(user.user_id)
          ? { ...user, is_active: isActive }
          : user
      )
    );

    try {
      // API call to update user status for each selected user
      await Promise.all(
        usersToUpdate.map(user =>
          api.patch(`/users/${user.user_id}`, {
            ...user,
            is_active: isActive
          })
        )
      );

      // Refresh users list after successful updates to ensure data consistency
      await fetchUsers();
    } catch (error) {
      // Revert optimistic update on error
      await fetchUsers();
      throw error;
    }

    // Clear selection after successful update
    setSelectedUsers(new Set());
  }, [fetchUsers]);

  const handleBulkStatusUpdate = useCallback(async (isActive: boolean) => {
    if (selectedUsers.size === 0) return;

    setBulkActionLoading(true);
    try {
      // Get user data for selected users
      const selectedUserData = users.filter(user => selectedUsers.has(user.user_id));

      // Protection: Don't deactivate admin users
      if (!isActive) {
        const adminUsers = selectedUserData.filter(user => user.role === 'admin');
        if (adminUsers.length > 0) {
          const adminEmails = adminUsers.map(u => u.email).join(', ');

          // Show confirmation dialog instead of using confirm()
          const confirmed = globalThis.confirm(`⚠️  PAŽNJA: Pokušavate da deaktivirate admin korisnike: ${adminEmails}\n\nOvo može dovesti do gubitka pristupa sistemu. Da li ste sigurni?`);
          if (!confirmed) {
            setBulkActionLoading(false);
            return;
          }
        }
      }

      // Use helper function to perform the update
      await performBulkStatusUpdate(isActive, selectedUserData);

    } catch (error) {
      logger.error('Error updating user status:', error);
      // Show error message to user
      alert(`Greška pri ${isActive ? 'aktiviranju' : 'deaktiviranju'} korisnika: ${error}`);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedUsers, users, performBulkStatusUpdate]);





  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ResponsivePageHeader
        title="Upravljanje Korisnicima"
        subtitle="Administracija korisničkih naloga i dozvola"
        userInfo={{
          name: currentUser?.email?.split('@')[0] || 'Korisnik',
          email: currentUser?.email,
          company: currentUser?.company_name || 'OmniPass Portal',
          role: currentUser?.role
        }}
        actions={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            {/* User Limit Status */}
            {userLimitStatus && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {userLimitStatus.current_users} / {userLimitStatus.max_users} korisnika
                </Typography>
                <Box
                  sx={{
                    width: 100,
                    height: 6,
                    bgcolor: 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(userLimitStatus.percentage_used, 100)}%`,
                      height: '100%',
                      bgcolor: userLimitStatus.can_add_user ? 'success.main' : 'error.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Add User Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
              disabled={!userLimitStatus?.can_add_user}
            >
              Dodaj korisnika
            </Button>

            {/* Upgrade CTA when limit reached */}
            {!userLimitStatus?.can_add_user && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                Dostigli ste limit korisnika. Nadogradite paket za više.
              </Typography>
            )}
          </Box>
        }
      />

      {/* Statistics Cards */}
      <Box display="flex" gap={2} mb={3} sx={{ flexWrap: 'wrap' }}>
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Ukupno Korisnika
                </Typography>
                <Typography variant="h4" color="primary">
                  {stats.totalUsers}
                </Typography>
              </CardContent>
            </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Administratori
            </Typography>
            <Typography variant="h4" color="error">
              {stats.adminCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Klijenti
            </Typography>
            <Typography variant="h4" color="primary">
              {stats.customerCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Aktivni
            </Typography>
            <Typography variant="h4" color="success">
              {stats.activeCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Search */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            placeholder="Pretraži po email-u, kompaniji ili kontakt osobi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }
            }}
            sx={{ minWidth: 300, flex: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Uloga</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              label="Uloga"
            >
              <MenuItem value="all">Sve Uloge</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="customer">Klijent</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Bulk Action Toolbar */}
      {selectedUsers.size > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Toolbar
            sx={{
              bgcolor: alpha('#14264a', 0.1),
              borderRadius: 1,
              minHeight: { xs: 'auto', sm: '64px' },
              p: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Box display="flex" alignItems="center" sx={{ mb: { xs: 1, sm: 0 } }}>
              <CheckCircleOutlineIcon color="primary" sx={{ mr: 1 }} />
              <Typography
                color="primary"
                variant="subtitle1"
                fontWeight={600}
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                {selectedUsers.size} korisnik{selectedUsers.size === 1 ? '' : 'a'} označeno
              </Typography>
            </Box>

            <Box
              display="flex"
              gap={1}
              sx={{
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                ml: { xs: 0, sm: 'auto' }
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleBulkRoleUpdate('admin')}
                disabled={bulkActionLoading}
                startIcon={<AdminIcon />}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Admin
              </Button>

              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleBulkRoleUpdate('customer')}
                disabled={bulkActionLoading}
                startIcon={<BusinessIcon />}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Klijent
              </Button>

              <Button
                variant="outlined"
                color="success"
                size="small"
                onClick={() => handleBulkStatusUpdate(true)}
                disabled={bulkActionLoading}
                startIcon={<CheckCircleOutlineIcon />}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Aktiviraj
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleBulkStatusUpdate(false)}
                disabled={bulkActionLoading}
                startIcon={<RadioButtonUncheckedIcon />}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Deaktiviraj
              </Button>

              <Button
                variant="text"
                size="small"
                onClick={() => setSelectedUsers(new Set())}
                disabled={bulkActionLoading}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Poništi
              </Button>
            </Box>
          </Toolbar>
        </Paper>
      )}

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    indeterminate={
                      selectedUsers.size > 0 && selectedUsers.size < filteredUsers.length
                    }
                    checked={
                      filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length
                    }
                    onChange={handleSelectAll}
                    color="primary"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleOutlineIcon />}
                    indeterminateIcon={<SelectAllIcon />}
                  />
                </TableCell>
                <TableCell>Korisnik</TableCell>
                <TableCell>Kompanija</TableCell>
                <TableCell>Uloga</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Kreiran</TableCell>
                <TableCell>Poslednja Prijava</TableCell>
                <TableCell align="right">Akcije</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user, index) => (
                <TableRow key={user.user_id || `currentUser-${index}`} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={user.user_id ? selectedUsers.has(user.user_id.toString()) : false}
                      onChange={() => user.user_id && handleSelectUser(user.user_id.toString())}
                      color="primary"
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleOutlineIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {user.email[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : 'Ime nije postavljeno'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.company_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getRoleBadge(user.role)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Aktivan' : 'Neaktivan'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(user.created_at).toLocaleDateString('sr-RS')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString('sr-RS')
                        : 'Nikad'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => {
                        setSelectedUser(user);
                        setActionMenuAnchor(e.currentTarget);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Redova po stranici:"
          labelDisplayedRows={({ from, to, count }) => {
            const total = count !== -1 ? count : `više od ${to}`;
            return `${from}–${to} od ${total}`;
          }}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedUser && handleEditUser(selectedUser)}>
          <EditIcon sx={{ mr: 1 }} />
          Uredi
        </MenuItem>
        <MenuItem onClick={() => selectedUser && handleResetPassword(selectedUser)}>
          <VpnKeyIcon sx={{ mr: 1 }} />
          Resetuj lozinku
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleDeleteUser(selectedUser)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Obriši
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dodaj Novog Korisnika</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Ime"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                error={!!errors.first_name}
                helperText={errors.first_name}
                fullWidth
              />
              <TextField
                label="Prezime"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                error={!!errors.last_name}
                helperText={errors.last_name}
                fullWidth
              />
            </Box>

            <TextField
              label="Broj telefona"
              value={formData.phone || ''}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              error={!!errors.phone}
              helperText={errors.phone || "Opcionalno - kontakt telefon korisnika"}
              fullWidth
              placeholder="+381 60 123 4567"
            />

            <Alert severity="info" icon={<SecurityIcon />}>
              <Typography variant="body2" fontWeight="bold">
                🔐 Automatska Generacija Lozinke
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Sistem će automatski generisati privremenu lozinku (16 karaktera) i poslati je na email korisnika.
                Korisnik će biti obavezan da promeni lozinku pri prvom prijavljivanju.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 'bold' }}>
                📧 Email sadrži:
              </Typography>
              <Typography variant="caption" component="div">
                • Pristupne podatke (email + privremena lozinka)<br />
                • Link ka login stranici<br />
                • Sigurnosna upozorenja
              </Typography>
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Uloga</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value as any})}
                label="Uloga"
              >
                <MenuItem value="admin">Administrator</MenuItem>
                <MenuItem value="customer">Klijent</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth error={!!errors.company_name}>
              <InputLabel>Kompanija</InputLabel>
              <Select
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                label="Kompanija"
              >
                {companies.map((company) => (
                  <MenuItem key={company.company_id} value={company.company_name}>
                    {company.company_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.company_name && <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{errors.company_name}</Typography>}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>
            Otkaži
          </Button>
          <Button
            onClick={handleSubmitCreate}
            variant="contained"
            disabled={submitLoading}
          >
            {submitLoading ? <CircularProgress size={20} /> : 'Kreiraj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit currentUser Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Uredi Korisnika</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Ime"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                error={!!errors.first_name}
                helperText={errors.first_name}
                fullWidth
              />
              <TextField
                label="Prezime"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                error={!!errors.last_name}
                helperText={errors.last_name}
                fullWidth
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Uloga</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value as any})}
                label="Uloga"
              >
                <MenuItem value="admin">Administrator</MenuItem>
                <MenuItem value="customer">Klijent</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Kompanija"
              value={selectedUser?.company_name || ''}
              disabled
              fullWidth
              helperText="Kompanija se ne može menjati za postojeće korisnike"
            />

            <TextField
              label="Telefon"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>
            Otkaži
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            disabled={submitLoading}
          >
            {submitLoading ? <CircularProgress size={20} /> : 'Sačuvaj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Potvrdi Brisanje</DialogTitle>
        <DialogContent>
          <Typography>
            Da li ste sigurni da želite da obrišete korisnika <strong>{selectedUser?.email}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Ova akcija ne može da se opozove. Svi podaci vezani za ovog korisnika će biti trajno obrisani.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            Otkaži
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={submitLoading}
          >
            {submitLoading ? <CircularProgress size={20} /> : 'Obriši'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={isResetPasswordDialogOpen}
        onClose={() => {
          setIsResetPasswordDialogOpen(false);
          setResetPasswordResult(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resetuj Lozinku</DialogTitle>
        <DialogContent>
          {!resetPasswordResult ? (
            <>
              <Typography>
                Da li ste sigurni da želite da resetujete lozinku za korisnika{' '}
                <strong>{selectedUser?.email}</strong>?
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Sistem će automatski generisati novu privremenu lozinku i poslati je na email adresu korisnika.
              </Alert>
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Lozinka je uspešno resetovana!
              </Alert>

              <Typography variant="body2" sx={{ mb: 2 }}>
                Privremena lozinka: <strong>{resetPasswordResult.temporary_password}</strong>
              </Typography>

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(resetPasswordResult.temporary_password);
                  setNotification({
                    message: 'Lozinka kopirana u clipboard',
                    severity: 'success'
                  });
                }}
              >
                Kopiraj lozinku
              </Button>

              {resetPasswordResult.entra_id_reset && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Lozinka je resetovana u Microsoft Entra ID sistemu. Korisnik će biti primoran da promeni lozinku pri prvom logovanju.
                </Alert>
              )}

              {resetPasswordResult.email_sent ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Email sa uputstvima je poslat korisniku.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Email nije mogao biti poslat. Molimo da ručno prosledite privremenu lozinku korisniku.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!resetPasswordResult ? (
            <>
              <Button onClick={() => setIsResetPasswordDialogOpen(false)}>
                Otkaži
              </Button>
              <Button
                onClick={handleSubmitResetPassword}
                color="primary"
                variant="contained"
                disabled={submitLoading}
              >
                {submitLoading ? <CircularProgress size={20} /> : 'Resetuj Lozinku'}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setIsResetPasswordDialogOpen(false);
                setResetPasswordResult(null);
              }}
              variant="contained"
            >
              Zatvori
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
